import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Lock, MessageSquare, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const SUNSET = '#FF5733';

const Q1_PLACEHOLDER =
    'e.g. "Multipart upload resume after network drop is rock-solid, but the progress indicator briefly stutters at 99% before finalizing. Would love a smoother tail-end animation."';
const Q2_PLACEHOLDER =
    'e.g. "Owner-side R2 delete occasionally 500s when the object is > 100 GB — retry works, but a proactive re-queue would prevent user confusion."';

/**
 * Tab 8 — Feedback.
 *
 * Days 1–9: form padlocked; render the 10-dot login tracker.
 * Day 10+ : form unlocks. 72h cooldown between submits, max 4 entries,
 *           +50 GB each (cap +200 GB feedback total).
 *
 * Placeholders disappear on focus/input.
 */
export default function FeedbackTab() {
    const { user, refresh } = useAuth();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q1, setQ1] = useState('');
    const [q2, setQ2] = useState('');
    const [busy, setBusy] = useState(false);
    const [countdown, setCountdown] = useState('');

    const load = async () => {
        try {
            const { data } = await api.get('/beta/feedback/state');
            setState(data);
        } catch (_) {
            setState({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // Live countdown for the 72h cooldown.
    useEffect(() => {
        if (!state?.cooldown_ends_at) { setCountdown(''); return; }
        const tick = () => {
            const end = new Date(state.cooldown_ends_at).getTime();
            const now = Date.now();
            const diff = end - now;
            if (diff <= 0) { setCountdown(''); load(); return; }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            setCountdown(`${h}h ${m}m ${s}s`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [state?.cooldown_ends_at]);

    const submit = async (e) => {
        e.preventDefault();
        if (!q1.trim() || !q2.trim()) {
            toast.error('Please answer both questions.');
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post('/beta/feedback/submit', {
                q1: q1.trim(),
                q2: q2.trim(),
            });
            setState(data);
            setQ1('');
            setQ2('');
            toast.success('+50 GB unlocked · thank you for the deep test!');
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not submit feedback');
        } finally {
            setBusy(false);
        }
    };

    const dots = useMemo(() => {
        const filled = Math.min(state?.daily_logins_count ?? 0, 10);
        return { filled, empty: 10 - filled };
    }, [state?.daily_logins_count]);

    if (loading || !user) {
        return (
            <div
                data-testid="feedback-tab-loading"
                className="py-10 text-muted-foreground text-sm"
            >
                Loading feedback…
            </div>
        );
    }

    const unlocked = !!state?.unlocked;
    const entriesUsed = state?.entries_used ?? 0;
    const entriesCap = state?.entries_cap ?? 4;
    const totalGb = state?.total_awarded_gb ?? 0;
    const totalCap = state?.total_awarded_cap_gb ?? 200;
    const cooldownActive = !!state?.cooldown_ends_at;
    const capReached = entriesUsed >= entriesCap;
    const formDisabled = !unlocked || cooldownActive || capReached || busy;

    return (
        <div className="space-y-8" data-testid="feedback-tab-root">
            {/* Header — always visible */}
            <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Deep-test the pipeline
                </p>
                <h2 className="mt-2 font-heading text-2xl leading-tight">
                    {unlocked
                        ? 'The Feedback Loop is unlocked.'
                        : 'The Feedback Loop unlocks on day 10.'}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                    {unlocked
                        ? `Submit up to ${entriesCap} deep testing reports for +50 GB each (max +${totalCap} GB total). 72-hour cooldown between reports keeps the signal high.`
                        : `Log in each day. On the 10th unique login day this form unlocks, awarding up to +${totalCap} GB across ${entriesCap} deep-test reports.`}
                </p>
            </div>

            {/* Unpaid: counter frozen at 0 until a seat is claimed */}
            {state?.is_paid === false && (
                <div
                    data-testid="feedback-unpaid-banner"
                    className="flex items-start gap-3 rounded-lg border p-4 bg-card"
                    style={{ borderColor: `${SUNSET}55`, backgroundColor: `${SUNSET}0A` }}
                >
                    <Lock className="h-4 w-4 shrink-0 mt-0.5" style={{ color: SUNSET }} />
                    <p className="text-sm leading-relaxed">
                        🔒 Your login tracker is frozen at 0. Day 1 begins the moment you upgrade to a paid plan — grab a seat to start earning storage.
                    </p>
                </div>
            )}

            {/* Login progress dot tracker */}
            <Card className="p-6 bg-card border-border" data-testid="feedback-login-tracker">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Daily login tracker
                    </p>
                    <span className="text-xs text-muted-foreground">
                        Feedback storage earned: <span className="font-mono text-foreground">+{totalGb} / {totalCap} GB</span>
                    </span>
                </div>
                <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                    {Array.from({ length: 10 }).map((_, i) => {
                        const filled = i < dots.filled;
                        return (
                            <div
                                key={i}
                                data-testid={`feedback-login-dot-${i}`}
                                data-filled={filled}
                                className="h-6 w-6 rounded-full transition-all duration-300 border-2"
                                style={{
                                    backgroundColor: filled ? SUNSET : 'transparent',
                                    borderColor: filled ? SUNSET : 'rgba(255,255,255,0.2)',
                                    boxShadow: filled ? `0 0 12px ${SUNSET}66` : 'none',
                                }}
                            />
                        );
                    })}
                </div>
                <p
                    data-testid="feedback-login-count"
                    className="mt-5 text-center font-heading text-lg"
                >
                    <span style={{ color: SUNSET }}>{dots.filled}</span>
                    <span className="text-muted-foreground">/10 Daily Logins Verified</span>
                </p>
            </Card>

            {/* Status pill: locked / cooldown / cap reached / open */}
            {!unlocked && (
                <div
                    data-testid="feedback-locked-badge"
                    className="border border-border p-4 bg-card flex items-center gap-3"
                >
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                        Form locked until you complete <span className="text-foreground font-medium">10 daily logins</span>. Come back tomorrow.
                    </p>
                </div>
            )}
            {unlocked && cooldownActive && (
                <div
                    data-testid="feedback-cooldown-badge"
                    className="border border-[#FF5733]/40 p-4 bg-[#FF5733]/5 flex items-center gap-3"
                >
                    <Clock className="h-4 w-4" style={{ color: SUNSET }} />
                    <p className="text-sm">
                        <span className="font-medium" style={{ color: SUNSET }}>72h cooldown active.</span>
                        <span className="text-muted-foreground ml-2">
                            Next report unlocks in <span className="font-mono text-foreground">{countdown || '—'}</span>.
                        </span>
                    </p>
                </div>
            )}
            {unlocked && capReached && (
                <div
                    data-testid="feedback-cap-badge"
                    className="border border-[#FF5733]/40 p-4 bg-[#FF5733]/5 flex items-center gap-3"
                >
                    <CheckCircle2 className="h-4 w-4" style={{ color: SUNSET }} />
                    <p className="text-sm">
                        <span className="font-medium" style={{ color: SUNSET }}>Max reports reached.</span>
                        <span className="text-muted-foreground ml-2">
                            You&apos;ve submitted {entriesUsed}/{entriesCap} and locked in the full +{totalCap} GB feedback bonus. 🚀
                        </span>
                    </p>
                </div>
            )}

            {/* The two-question form */}
            <form onSubmit={submit}>
                <Card className="p-6 sm:p-8 bg-card border-border space-y-6">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: SUNSET }}>
                            Question 1
                        </p>
                        <label
                            htmlFor="feedback-q1"
                            className="mt-2 block font-heading text-lg leading-snug"
                        >
                            What features are working well, but could use some minor core refinement?
                        </label>
                        <Textarea
                            id="feedback-q1"
                            data-testid="feedback-q1-input"
                            value={q1}
                            onChange={(e) => setQ1(e.target.value)}
                            placeholder={Q1_PLACEHOLDER}
                            rows={4}
                            disabled={formDisabled}
                            className="mt-3 bg-background disabled:opacity-60"
                        />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.28em]" style={{ color: SUNSET }}>
                            Question 2
                        </p>
                        <label
                            htmlFor="feedback-q2"
                            className="mt-2 block font-heading text-lg leading-snug"
                        >
                            What parts of the app feel problematic or require significant adjustments?
                        </label>
                        <Textarea
                            id="feedback-q2"
                            data-testid="feedback-q2-input"
                            value={q2}
                            onChange={(e) => setQ2(e.target.value)}
                            placeholder={Q2_PLACEHOLDER}
                            rows={4}
                            disabled={formDisabled}
                            className="mt-3 bg-background disabled:opacity-60"
                        />
                    </div>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <p className="text-[11px] text-muted-foreground">
                            {entriesUsed}/{entriesCap} reports submitted · +50 GB per report
                        </p>
                        <Button
                            type="submit"
                            data-testid="feedback-submit-btn"
                            disabled={formDisabled}
                            className="text-white h-11 disabled:opacity-50"
                            style={{ backgroundColor: SUNSET }}
                        >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            {busy ? 'Sending…' : 'Submit report · +50 GB'}
                        </Button>
                    </div>
                </Card>
            </form>

            {/* Safety-vault footer copy */}
            <div className="text-[11px] text-muted-foreground leading-relaxed max-w-2xl">
                💼 <span className="font-medium text-foreground">1-Month Safety Vault:</span>{' '}
                Earned beta storage and queued rewards carry forward for exactly 30 days after the beta cycle concludes.
                Stale un-upgraded accounts forfeit accumulated perks after Day 30.
            </div>
        </div>
    );
}
